import { Box, Button, Flex, HStack, IconButton, Text, useBreakpointValue } from 'design-system';
import { usePageParams } from 'hooks/use-page-params';
import { FamilyListPanel } from 'pages/prices/components/family-list-panel';
import { PriceMatrix } from 'pages/prices/components/price-matrix';
import { TopProductsPanel } from 'pages/prices/components/top-products-panel';
import { useEffect } from 'react';
import { FiArrowLeft } from 'react-icons/fi';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';

type PricesView = 'matrix' | 'top';

// One joined surface per the design handoff: family list and matrix are two
// panes of a single card separated by an internal divider, not sibling cards.
// The `view` URL param switches the card between the matrix and the Daily
// Update List worklist.
export const PricesScreen = () => {
  const dispatch = useAppDispatch();
  const { getPageParams, setPageParams } = usePageParams();
  const params = getPageParams();
  const familyId = Number(params.familyId) || undefined;
  const view: PricesView = params.view === 'top' ? 'top' : 'matrix';

  // Below md the joined card shows one pane at a time: the family list until a
  // family is picked, then the matrix with a back affordance.
  const isMobile = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    dispatch(changeTitle('Prices'));
  }, []);

  const selectFamily = (id: number) => {
    setPageParams({ ...getPageParams(), familyId: id });
  };

  // Mobile back affordance: symmetric to selectFamily — clearing the param
  // returns the card to the family list pane.
  const clearFamily = () => {
    setPageParams({ ...getPageParams(), familyId: undefined });
  };

  const setView = (next: PricesView) => {
    setPageParams({ ...getPageParams(), view: next === 'matrix' ? undefined : next });
  };

  // A worklist row opens its family in the matrix: queue -> workbench.
  const openFamilyInMatrix = (id: number) => {
    setPageParams({ ...getPageParams(), view: undefined, familyId: id });
  };

  const tab = (value: PricesView, label: string) => (
    <Button
      size='sm'
      variant='ghost'
      borderRadius='8px'
      fontWeight={view === value ? 'semibold' : 'medium'}
      color={view === value ? 'red.600' : 'gray.600'}
      bg={view === value ? 'white' : 'transparent'}
      boxShadow={view === value ? 'sm' : 'none'}
      _hover={{ bg: view === value ? 'white' : 'gray.100' }}
      onClick={() => setView(value)}>
      {label}
    </Button>
  );

  return (
    <Flex direction='column' h={{ base: '100%', md: 'calc(100vh - 160px)' }}>
      <Flex justify='space-between' align='center' mb='12px' wrap='wrap' rowGap='8px'>
        <HStack spacing='4px' bg='#F1F1F4' borderRadius='10px' p='3px'>
          {tab('matrix', 'Price Matrix')}
          {tab('top', 'Daily Update List')}
        </HStack>
      </Flex>

      <Flex
        align='stretch'
        flex='1'
        minH={0}
        bg='white'
        border='1px solid'
        borderColor='gray.200'
        borderRadius='md'
        overflow='hidden'>
        {view === 'top' ? (
          <Box flex='1' minW={0}>
            <TopProductsPanel onOpenFamily={openFamilyInMatrix} />
          </Box>
        ) : (
          <>
            {(!isMobile || !familyId) && <FamilyListPanel selectedFamilyId={familyId} onSelect={selectFamily} />}

            {(!isMobile || familyId) && (
              <Box flex='1' minW={0}>
                {familyId ? (
                  isMobile ? (
                    <Flex direction='column' h='full'>
                      <Flex align='center' gap='4px' p='6px 8px' borderBottom='1px solid' borderColor='gray.200'>
                        <IconButton
                          aria-label='Back to families'
                          icon={<FiArrowLeft size={16} />}
                          size='sm'
                          variant='ghost'
                          onClick={clearFamily}
                        />
                        <Text fontSize='13px' fontWeight='medium' color='gray.600'>
                          Families
                        </Text>
                      </Flex>
                      <Box flex='1' minH={0}>
                        <PriceMatrix familyId={familyId} />
                      </Box>
                    </Flex>
                  ) : (
                    <PriceMatrix familyId={familyId} />
                  )
                ) : (
                  <Flex justify='center' align='center' h='full'>
                    <Text fontSize='13.5px' color='gray.500'>
                      Select a family to see its supplier prices
                    </Text>
                  </Flex>
                )}
              </Box>
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};
